import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiHeart,
} from "react-icons/fi";
import productData, { siteConfig } from "../Datas/productData";
import { CartContext } from "./CartContext";
import { ProductCard } from "./Shop";
import "../css/productdetails.scss";

const colorDetails = (color) =>
  typeof color === "string"
    ? { name: color, hex: "#b8b8b5" }
    : { name: color?.name || "Colour", hex: color?.hex || "#b8b8b5" };

const wishlistItemId = (item) =>
  typeof item === "object" && item !== null ? item.id : item;

const formatPrice = (price) => {
  if (typeof siteConfig?.formatPrice === "function") {
    return siteConfig.formatPrice(price);
  }

  return `${siteConfig?.currencySymbol || "$"} ${Number(price).toLocaleString(
    "en-US"
  )}`;
};

const formatAttributeName = (name) =>
  name
    .replace(/([A-Z])/g, " $1")
    .replace(/[-_]/g, " ")
    .replace(/^./, (letter) => letter.toUpperCase());

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const cartContext = useContext(CartContext) || {};
  const addToCart = cartContext.addToCart || (() => {});
  const wishlistItems = cartContext.wishlistItems || [];
  const toggleWishlist = cartContext.toggleWishlist || (() => {});
  const product = productData.find((item) => String(item.id) === String(id));

  const images = useMemo(
    () =>
      product
        ? [...new Set([...(product.images || []), product.image].filter(Boolean))]
        : [],
    [product]
  );
  const colors = useMemo(
    () => (product?.colors || []).map(colorDetails),
    [product]
  );

  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState(colors[0]?.name || "");
  const [selectedImage, setSelectedImage] = useState(0);
  const [failedImages, setFailedImages] = useState([]);
  const [sizeError, setSizeError] = useState("");
  const sizeSelectRef = useRef(null);

  useEffect(() => {
    setSelectedSize("");
    setSelectedColor(colors[0]?.name || "");
    setSelectedImage(0);
    setFailedImages([]);
    setSizeError("");
  }, [colors, product]);

  const recommendations = useMemo(() => {
    if (!product) return [];

    const candidates = [
      ...productData.filter(
        (item) => item.id !== product.id && item.category === product.category
      ),
      ...productData.filter(
        (item) => item.id !== product.id && item.gender === product.gender
      ),
      ...productData.filter((item) => item.id !== product.id),
    ];

    return candidates
      .filter(
        (item, index) =>
          candidates.findIndex((candidate) => candidate.id === item.id) === index
      )
      .slice(0, 4);
  }, [product]);

  if (!product) {
    return (
      <main className="product-detail product-detail--not-found" id="main-content">
        <div className="product-detail__not-found">
          <p>404</p>
          <h1>Product not found</h1>
          <Link to="/shop">
            <FiChevronLeft aria-hidden="true" />
            Back to shop
          </Link>
        </div>
      </main>
    );
  }

  const isWishlisted = wishlistItems.some(
    (item) => String(wishlistItemId(item)) === String(product.id)
  );
  const description = Array.isArray(product.description)
    ? product.description.join(" ")
    : product.description;
  const features = product.features || product.Features || [];
  const attributes = Object.entries(product.attributes || {});
  const isSoldOut = Number(product.stock) <= 0;

  const changeImage = (direction) => {
    setSelectedImage((current) =>
      (current + direction + images.length) % images.length
    );
  };

  const markImageAsFailed = (image) => {
    setFailedImages((current) =>
      current.includes(image) ? current : [...current, image]
    );
  };

  const handleAddToCart = () => {
    if (isSoldOut || !selectedColor) return;

    if (!selectedSize) {
      setSizeError("Please select a size before adding this item to your bag.");
      sizeSelectRef.current?.focus();
      return;
    }

    setSizeError("");
    addToCart(product.id, 1, selectedSize, selectedColor);
    navigate("/cartpage");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="product-detail" id="main-content">
      <nav className="pdp-breadcrumb" aria-label="Breadcrumb">
        <Link to="/shop">Shop</Link>
        <span aria-hidden="true">/</span>
        <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>
          {product.category}
        </Link>
        <span aria-hidden="true">/</span>
        <span>{product.name}</span>
      </nav>

      <div className="pdp-layout">
        <section className="pdp-gallery" aria-label={`${product.name} images`}>
          {images.map((image, index) => (
            <figure className="pdp-gallery__item" key={image}>
              {failedImages.includes(image) ? (
                <span className="pdp-gallery__error">Image unavailable</span>
              ) : (
                <img
                  src={image}
                  alt={`${product.name}, view ${index + 1}`}
                  loading={index < 2 ? "eager" : "lazy"}
                  onError={() => markImageAsFailed(image)}
                />
              )}
            </figure>
          ))}
        </section>

        <section className="pdp-mobile-gallery" aria-label={`${product.name} images`}>
          <div className="pdp-mobile-gallery__image">
            {failedImages.includes(images[selectedImage]) ? (
              <span className="pdp-gallery__error">Image unavailable</span>
            ) : (
              <img
                src={images[selectedImage]}
                alt={`${product.name}, view ${selectedImage + 1}`}
                onError={() => markImageAsFailed(images[selectedImage])}
              />
            )}
          </div>

          {images.length > 1 && (
            <>
              <button
                className="pdp-mobile-gallery__arrow pdp-mobile-gallery__arrow--previous"
                type="button"
                onClick={() => changeImage(-1)}
                aria-label="Previous image"
              >
                <FiChevronLeft aria-hidden="true" />
              </button>
              <button
                className="pdp-mobile-gallery__arrow pdp-mobile-gallery__arrow--next"
                type="button"
                onClick={() => changeImage(1)}
                aria-label="Next image"
              >
                <FiChevronRight aria-hidden="true" />
              </button>
              <div className="pdp-mobile-gallery__dots" aria-label="Choose product image">
                {images.map((image, index) => (
                  <button
                    className={selectedImage === index ? "is-active" : ""}
                    key={image}
                    type="button"
                    onClick={() => setSelectedImage(index)}
                    aria-label={`Show image ${index + 1}`}
                    aria-current={selectedImage === index ? "true" : undefined}
                  />
                ))}
              </div>
            </>
          )}
        </section>

        <aside className="pdp-info">
          <div className="pdp-info__sticky">
            <div className="pdp-info__heading">
              <div>
                {product.badge && <p className="pdp-info__badge">{product.badge}</p>}
                <h1>{product.name}</h1>
                <p className="pdp-info__price">{formatPrice(product.price)}</p>
              </div>
              <button
                className={`pdp-info__wishlist ${isWishlisted ? "is-active" : ""}`}
                type="button"
                onClick={() => toggleWishlist(product.id)}
                aria-label={`${isWishlisted ? "Remove" : "Add"} ${
                  product.name
                } ${isWishlisted ? "from" : "to"} wishlist`}
                aria-pressed={isWishlisted}
                title={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
              >
                <FiHeart aria-hidden="true" />
              </button>
            </div>

            <p className="pdp-info__description">{description}</p>

            {colors.length > 0 && (
              <fieldset className="pdp-colors">
                <legend>
                  Colour: <span>{selectedColor}</span>
                </legend>
                <div className="pdp-colors__options">
                  {colors.map((color) => (
                    <button
                      className={selectedColor === color.name ? "is-active" : ""}
                      key={color.name}
                      type="button"
                      onClick={() => setSelectedColor(color.name)}
                      aria-label={`Select ${color.name}`}
                      aria-pressed={selectedColor === color.name}
                      title={color.name}
                    >
                      <span style={{ backgroundColor: color.hex }} />
                    </button>
                  ))}
                </div>
              </fieldset>
            )}

            <div className="pdp-size-field">
              <label className="pdp-size">
                <span>Select a size</span>
                <span className="pdp-size__select">
                  <select
                    ref={sizeSelectRef}
                    value={selectedSize}
                    onChange={(event) => {
                      setSelectedSize(event.target.value);
                      setSizeError("");
                    }}
                    disabled={isSoldOut}
                    aria-invalid={Boolean(sizeError)}
                    aria-describedby={sizeError ? "pdp-size-error" : undefined}
                  >
                    <option value="" disabled>
                      Select size
                    </option>
                    {(product.size || []).map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  <FiChevronDown aria-hidden="true" />
                </span>
              </label>
              {sizeError && (
                <p className="pdp-size__error" id="pdp-size-error" role="alert">
                  {sizeError}
                </p>
              )}
            </div>

            <div className="pdp-purchase">
              <button
                className="pdp-add"
                type="button"
                onClick={handleAddToCart}
                disabled={isSoldOut || !selectedColor}
              >
                {isSoldOut ? "Sold out" : "Add to bag"}
              </button>
            </div>

            {!isSoldOut && (
              <p className="pdp-info__availability">
                In stock. Standard tracked shipping is{" "}
                {formatPrice(siteConfig.standardShipping)} per order.
              </p>
            )}

            <div className="pdp-accordions">
              <details open>
                <summary>
                  Details &amp; fit
                  <FiChevronDown aria-hidden="true" />
                </summary>
                <div className="pdp-accordions__content">
                  {features.length > 0 && (
                    <ul>
                      {features.map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                  )}
                  {product.material && <p>{product.material}</p>}
                  {attributes.map(([name, value]) => (
                    <p key={name}>
                      <strong>{formatAttributeName(name)}:</strong> {String(value)}
                    </p>
                  ))}
                </div>
              </details>

              <details>
                <summary>
                  Payments &amp; shipping
                  <FiChevronDown aria-hidden="true" />
                </summary>
                <div className="pdp-accordions__content">
                  <p>
                    Secure payment by major credit card. Standard delivery in 2-5
                    working days.
                  </p>
                  <p>
                    Returns are complimentary within 14 days when items are unworn
                    and returned with their original packaging.
                  </p>
                  <Link to="/shipping">Shipping information</Link>
                  <Link to="/returns">Returns information</Link>
                </div>
              </details>

              <details>
                <summary>
                  Customer service
                  <FiChevronDown aria-hidden="true" />
                </summary>
                <div className="pdp-accordions__content">
                  <p>Our client service team is available Monday through Friday.</p>
                  <Link to="/contact">Contact us</Link>
                </div>
              </details>
            </div>
          </div>
        </aside>
      </div>

      {recommendations.length > 0 && (
        <section className="pdp-recommendations" aria-labelledby="recommendations-title">
          <div className="pdp-recommendations__heading">
            <h2 id="recommendations-title">You may also like</h2>
            <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>
              View all
            </Link>
          </div>
          <div className="pdp-recommendations__grid">
            {recommendations.map((item) => (
              <ProductCard key={item.id} product={item} />
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default ProductDetail;
